/**
 * vendorDiscounts.js
 *
 * The customer-side view of a vendor discount, in one place.
 *
 * Every customer-facing product object carries `price` (the vendor's LIST
 * price), `listPrice` (the same number), `salePrice` (a number while a
 * discount is live, otherwise null) and `discount` (the reason for the cut,
 * or null). The server re-prices every order line from the catalogue, so the
 * cart, the checkout totals and the confirmation all have to show the number
 * it will charge: `salePrice ?? price`.
 */

/* `Number(null)` and `Number("")` are both 0, and 0 is finite, so coercing
   first would read "no sale price" as a sale price of zero. The server sends
   `salePrice: null` on every product that is NOT discounted, so absent has to
   stay absent here. Booleans coerce to 0/1 and are not prices either. */
const finite = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * True while the product carries a live sale price: a real number that is
 * genuinely below the list price. This is the same test the mobile clients
 * use, so the three storefronts agree on what counts as a discount.
 */
export const hasSalePrice = (product) => {
  const sale = finite(product?.salePrice);
  if (sale === null) return false;
  const list = finite(product?.listPrice ?? product?.price);
  return list === null ? true : sale < list;
};

/** The unit price the customer pays: the sale price while one is live. Reads
    the same test as hasSalePrice, so what is charged is what is shown. */
export const unitPriceOf = (product) => {
  if (hasSalePrice(product)) return finite(product.salePrice) ?? 0;
  return finite(product?.price) ?? 0;
};

/** The vendor's list price, which `price` still is until the cart takes it. */
export const listPriceOf = (product) => {
  const list = finite(product?.listPrice);
  if (list !== null) return list;
  return finite(product?.price) ?? 0;
};

/**
 * The shape a cart line stores: `price` becomes the unit price the customer
 * pays, and the list price plus the discount ride along so the cart can show
 * the strike-through and the "Vendor offer" savings line. Safe to call on a
 * line that already went through it, because `salePrice` stays on the line.
 */
export const withDealPricing = (product) => {
  if (!product || typeof product !== "object") return product;
  const listPrice = listPriceOf(product);
  if (!hasSalePrice(product)) {
    return {
      ...product,
      listPrice,
      salePrice: null,
      discount: product.discount ?? null,
    };
  }
  return {
    ...product,
    price: unitPriceOf(product),
    listPrice,
    salePrice: finite(product.salePrice),
    discount: product.discount ?? null,
  };
};

/** Just the four pricing fields, for refreshing a cart line that already exists. */
export const dealPricingFields = (product) => {
  const priced = withDealPricing(product);
  return {
    price: priced.price,
    listPrice: priced.listPrice,
    salePrice: priced.salePrice,
    discount: priced.discount,
  };
};

/** True when a cart line carries a vendor discount worth showing. */
export const lineHasDeal = (item) => {
  if (!item || !item.discount) return false;
  const list = finite(item.listPrice);
  const price = finite(item.price);
  return list !== null && price !== null && list > price;
};

/** What the vendor's offer took off ONE cart line: (listPrice - price) * qty. */
export const lineSavings = (item) => {
  if (!lineHasDeal(item)) return 0;
  const qty = finite(item.qty ?? item.quantity) ?? 0;
  if (qty <= 0) return 0;
  return (finite(item.listPrice) - finite(item.price)) * qty;
};

/** The "Vendor offer" total for a cart: the sum of every line's savings. */
export const cartOfferSavings = (items) =>
  (Array.isArray(items) ? items : Object.values(items || {})).reduce(
    (sum, item) => sum + lineSavings(item),
    0,
  );

/** The amount a live sale price takes off one unit, for "You save" copy. */
export const unitSavingsOf = (product) => {
  if (!hasSalePrice(product)) return 0;
  const saved = listPriceOf(product) - unitPriceOf(product);
  return saved > 0 ? saved : 0;
};
